import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChartLayout, OverviewContent, MethodologyContent, RelevanceContent, EquityContent } from '../../../components/charts/ChartLayout';
import { KeyMetrics, KeyMetric } from '../../../components/charts/KeyMetrics';
import { californiaPumsService, CaliforniaPumaRecord } from '../services/californiaPumsService';
import { PumaResult } from '../services/pumsTypes';
import { getPumaName, getCountyFromPuma } from '../services/pumaNames';
import { getRegionName, getPumaCodesForRegion } from '../services/regionMapping';
import { preloadGeoJsonMapping, getCountyFromPumaGeoJson, getCountyFromPumaGeoJsonSync } from '../services/pumaGeoJsonService';
import BayAreaCountyMap from './BayAreaCountyMap';
import RegionSelector from './RegionSelector';

interface FilterState {
  // Multifamily unit size filters
  unitsUnder5: boolean;
  units5to19: boolean;
  units20to50: boolean;
  units50Plus: boolean;
  
  // Vintage filters
  vintagePre1980: boolean;
  vintage1980to2000: boolean;
  vintage2000Plus: boolean;
  
  // Heating fuel filters
  heatingElectric: boolean;
  heatingGas: boolean;
  heatingOther: boolean;
  
  // Tenure filters
  ownerOccupied: boolean;
  renterOccupied: boolean;
  
  // Rent burden filters
  lowRentBurden: boolean; // < 30%
  highRentBurden: boolean; // >= 30%
  
  // Energy burden filters
  lowEnergyBurden: boolean; // < 6%
  highEnergyBurden: boolean; // >= 6%
  
  // Demographic filters (householder only)
  seniorHouseholder: boolean; // 65+
  pocHouseholder: boolean; // Person of Color
  lowEducation: boolean; // Less than college degree
}

interface FilteredResult {
  puma: number;
  pumaName: string;
  county: string;
  totalHouseholds: number;
  filteredCount: number;
  percentage: number;
}

interface CaliforniaBayRENToolProps {
  onMetricsUpdate?: (metrics: any) => void;
}

const CaliforniaBayRENTool: React.FC<CaliforniaBayRENToolProps> = ({ onMetricsUpdate }) => {
  const [rawData, setRawData] = useState<CaliforniaPumaRecord[]>([]);
  const [allFilteredResults, setAllFilteredResults] = useState<FilteredResult[]>([]); // PUMAs for selected region (or all if "All California")
  const [filteredResults, setFilteredResults] = useState<FilteredResult[]>([]); // Same as allFilteredResults (since data is region-specific)
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0); // Loading progress percentage (0-100)
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState('overview');
  const [selectedRegion, setSelectedRegion] = useState<number>(1); // Default to Superior California
  const [geoJsonLoaded, setGeoJsonLoaded] = useState(false); // Track when GeoJSON mapping is loaded
  
  // Debounce filter changes to avoid processing on every keystroke
  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>({
    unitsUnder5: true,
    units5to19: true,
    units20to50: true,
    units50Plus: true,
    vintagePre1980: true,
    vintage1980to2000: true,
    vintage2000Plus: true,
    heatingElectric: true,
    heatingGas: true,
    heatingOther: true,
    ownerOccupied: true,
    renterOccupied: true,
    lowRentBurden: true,
    highRentBurden: true,
    lowEnergyBurden: true,
    highEnergyBurden: true,
    seniorHouseholder: true,
    pocHouseholder: true,
    lowEducation: true,
  });
  
  // Handle region change
  const handleRegionChange = (regionId: number) => {
    setSelectedRegion(regionId);
    // Data loading and reset will be handled by the useEffect
  };
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    // Default: show all unit sizes (all units)
    unitsUnder5: true,
    units5to19: true,
    units20to50: true,
    units50Plus: true,
    
    // Default: all vintages
    vintagePre1980: true,
    vintage1980to2000: true,
    vintage2000Plus: true,
    
    // Default: all heating fuels
    heatingElectric: true,
    heatingGas: true,
    heatingOther: true,
    
    // Default: all tenure
    ownerOccupied: true,
    renterOccupied: true,
    
    // Default: all rent burden levels
    lowRentBurden: true,
    highRentBurden: true,
    
    // Default: all energy burden levels
    lowEnergyBurden: true,
    highEnergyBurden: true,
    
    // Default: all demographics
    seniorHouseholder: true,
    pocHouseholder: true,
    lowEducation: true,
  });

  // Pre-load GeoJSON mapping for county names (same as map uses)
  useEffect(() => {
    const loadGeoJsonMapping = async () => {
      try {
        // Preload and wait for it to complete
        preloadGeoJsonMapping();
        
        // Wait for the mapping to build
        // The mapping is built asynchronously, so we'll poll until it's ready
        const startTime = Date.now();
        const checkMapping = async () => {
          // Test if mapping is loaded by checking a known PUMA (Alameda County)
          const testCounty = await getCountyFromPumaGeoJson(101);
          const elapsed = Date.now() - startTime;
          
          if (testCounty || elapsed > 5000) { // Wait up to 5 seconds
            setGeoJsonLoaded(true);
            console.log(`✅ GeoJSON mapping loaded for county lookups (${testCounty || 'timeout'})`);
          } else {
            // Retry after a short delay
            setTimeout(checkMapping, 200);
          }
        };
        
        // Start checking after a short delay to let mapping build
        setTimeout(checkMapping, 300);
      } catch (err) {
        console.error('Error loading GeoJSON mapping:', err);
        // Set loaded anyway so we can proceed without it
        setGeoJsonLoaded(true);
      }
    };
    
    loadGeoJsonMapping();
  }, []);

  // Load raw California PUMS data based on selected region
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setLoadingProgress(0);
        setError(null);
        
        const regionPumaCodes = getPumaCodesForRegion(selectedRegion);
        
        if (selectedRegion === 0) {
          // All California - show loading estimate
          console.log('Loading all California PUMS data (estimated ~30 seconds)...');
          const data = await californiaPumsService.getAllCaliforniaData((progress) => {
            setLoadingProgress(progress);
          });
          setRawData(data);
          setLoadingProgress(100);
          console.log(`Loaded ${data.length} California PUMS records`);
        } else if (regionPumaCodes.length > 0) {
          // Region-specific - much faster
          console.log(`Loading PUMS data for ${getRegionName(selectedRegion)} (${regionPumaCodes.length} PUMAs)...`);
          const data = await californiaPumsService.getDataByPumaCodes(regionPumaCodes, (progress) => {
            setLoadingProgress(progress);
          });
          setRawData(data);
          setLoadingProgress(100);
          console.log(`Loaded ${data.length} records for region`);
        } else {
          console.log('No PUMA codes for selected region');
          setRawData([]);
          setLoadingProgress(100);
        }
      } catch (err) {
        console.error('Error loading California PUMS data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setLoadingProgress(0);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedRegion]); // Load data when region changes

  // Calculate energy burden: (12 × (ELEP + GASP + FULP) / HINCP, adjusted with ADJINC)
  // Note: The table may have energy_burden pre-calculated, but we calculate it here for consistency
  const calculateEnergyBurden = useCallback((record: CaliforniaPumaRecord): number | null => {
    const elep = record.elep || 0;
    const gasp = record.gasp || 0;
    const fulp = record.fulp || 0;
    const hincp = record.hincp || 0;
    const adjinc = record.adjinc || 0;

    if (hincp <= 0) return null;

    const adjustedIncome = hincp * (adjinc / 1000000);
    if (adjustedIncome <= 0) return null;

    const annualEnergyCost = 12 * (elep + gasp + fulp);
    return annualEnergyCost / adjustedIncome;
  }, []);

  // Memoize meetsFilterCriteria to avoid recreating on every render
  const meetsFilterCriteria = useCallback((record: CaliforniaPumaRecord, filterState: FilterState): boolean => {
    // Multifamily unit size check
    const isMultifamily = 
      (filterState.unitsUnder5 && record.bld >= 1 && record.bld <= 2) ||
      (filterState.units5to19 && record.bld >= 3 && record.bld <= 4) ||
      (filterState.units20to50 && record.bld >= 5 && record.bld <= 6) ||
      (filterState.units50Plus && record.bld >= 7);
    
    if (!isMultifamily) return false;

    // Vintage check
    const vintageMatch = 
      (filterState.vintagePre1980 && record.yrblt < 1980) ||
      (filterState.vintage1980to2000 && record.yrblt >= 1980 && record.yrblt <= 2000) ||
      (filterState.vintage2000Plus && record.yrblt > 2000);
    
    if (!vintageMatch) return false;

    // Heating fuel check
    const heatingMatch = 
      (filterState.heatingElectric && record.hfl === 2) ||
      (filterState.heatingGas && record.hfl === 1) ||
      (filterState.heatingOther && record.hfl >= 3 && record.hfl <= 10);
    
    if (!heatingMatch) return false;

    // Tenure check
    const tenureMatch = 
      (filterState.ownerOccupied && record.ten === 1) ||
      (filterState.renterOccupied && record.ten === 2);
    
    if (!tenureMatch) return false;

    // Rent burden check (only for renters)
    if (record.ten === 2 && record.grpip !== null && record.grpip !== undefined && record.grpip > 0) {
      const rentBurdenMatch = 
        (filterState.lowRentBurden && record.grpip < 30) ||
        (filterState.highRentBurden && record.grpip >= 30);
      
      if (!rentBurdenMatch) return false;
    }

    // Energy burden check
    const energyBurden = calculateEnergyBurden(record);
    if (energyBurden !== null && energyBurden !== undefined && !isNaN(energyBurden)) {
      const energyBurdenMatch = 
        (filterState.lowEnergyBurden && energyBurden < 0.06) ||
        (filterState.highEnergyBurden && energyBurden >= 0.06);
      
      if (!energyBurdenMatch) return false;
    }

    // Demographic check (only for householder - RELSHIPP == 20)
    if (record.relshipp === 20) {
      // Check if any demographic filters are enabled
      const hasDemographicFilters = filterState.seniorHouseholder || filterState.pocHouseholder || filterState.lowEducation;
      
      if (hasDemographicFilters) {
        // If demographic filters are enabled, record must match at least one
        const demographicMatch = 
          (filterState.seniorHouseholder && record.agep >= 65) ||
          (filterState.pocHouseholder && !(record.rac1p === 1 && record.hisp === 1)) ||
          (filterState.lowEducation && record.schl < 21);
        
        if (!demographicMatch) return false;
      }
      // If no demographic filters are enabled, all records pass this check
    }

    return true;
  }, [calculateEnergyBurden]);

  // Helper functions
  // Pre-compute PUMA-to-records map (only recalculate when rawData changes)
  const pumaRecordsMap = useMemo(() => {
    if (rawData.length === 0) return new Map<number, CaliforniaPumaRecord[]>();
    
    const map = new Map<number, CaliforniaPumaRecord[]>();
    rawData.forEach(record => {
      if (!map.has(record.puma)) {
        map.set(record.puma, []);
      }
      map.get(record.puma)!.push(record);
    });
    return map;
  }, [rawData]);

  // Memoize filtered results calculation (only recalculate when dependencies change)
  const allFilteredResultsMemo = useMemo(() => {
    if (rawData.length === 0 || pumaRecordsMap.size === 0) return [];
    
    console.log('Applying filters to California PUMS data...');
    const allResults: FilteredResult[] = [];
    const pumas = Array.from(pumaRecordsMap.keys());
    
    // Process all PUMAs (use React's batching to avoid blocking)
    pumas.forEach(puma => {
      const records = pumaRecordsMap.get(puma) || [];
      
      let totalWeight = 0;
      let filteredWeight = 0;

      records.forEach(record => {
        const weight = record.wgtp || 0;
        totalWeight += weight;

        if (meetsFilterCriteria(record, debouncedFilters)) {
          filteredWeight += weight;
        }
      });

      if (totalWeight > 0) {
        allResults.push({
          puma,
          pumaName: getPumaName(puma),
          county: getCountyFromPuma(puma),
          totalHouseholds: Math.round(totalWeight),
          filteredCount: Math.round(filteredWeight),
          percentage: totalWeight > 0 ? (filteredWeight / totalWeight) * 100 : 0
        });
      }
    });
    
    // Sort by filtered count descending
    allResults.sort((a, b) => b.filteredCount - a.filteredCount);
    
    return allResults;
  }, [rawData, debouncedFilters, pumaRecordsMap, meetsFilterCriteria]);

  // Update county names when GeoJSON mapping becomes available
  useEffect(() => {
    if (!geoJsonLoaded || allFilteredResults.length === 0) return;
    
    const updateCountyNames = async () => {
      console.log('🔄 Updating county names from GeoJSON mapping...');
      console.log(`📊 Updating ${allFilteredResults.length} results`);
      
      // Update county names using async GeoJSON lookup
      const updatedResults = await Promise.all(
        allFilteredResults.map(async (result) => {
          // Try GeoJSON lookup first (most comprehensive)
          const geoJsonCounty = await getCountyFromPumaGeoJson(result.puma);
          
          // Debug logging for specific PUMAs
          if (result.county === 'Central Coast' || result.county === 'Central Valley' || result.county === 'Superior California') {
            console.log(`🔍 PUMA ${result.puma}: Current county="${result.county}", GeoJSON county="${geoJsonCounty || 'null'}"`);
          }
          
          if (geoJsonCounty && geoJsonCounty !== result.county) {
            return { ...result, county: geoJsonCounty };
          }
          // Fall back to existing county (might be from other mappings)
          return result;
        })
      );
      
      // Count how many counties changed
      const changedCount = updatedResults.filter((updated, index) => 
        updated.county !== allFilteredResults[index].county
      ).length;
      
      if (changedCount > 0) {
        console.log(`✅ Updated ${changedCount} county names from GeoJSON mapping`);
        setAllFilteredResults(updatedResults);
        
        // Since we now load region-specific data, all results are already for the selected region
        setFilteredResults(updatedResults);
      } else {
        console.log('⚠️ No county name changes detected - GeoJSON mapping may not have these PUMAs');
      }
    };
    
    updateCountyNames();
  }, [geoJsonLoaded, allFilteredResults, selectedRegion]);

  // Update state when memoized results change
  // Since we now load region-specific data, allFilteredResults and filteredResults will be the same
  // unless "All California" is selected (region 0), in which case we still filter for table display
  useEffect(() => {
    setAllFilteredResults(allFilteredResultsMemo);
    
    // If "All California" is selected, filter results for table display
    // Otherwise, since we loaded region-specific data, all results are already for the selected region
    if (selectedRegion === 0) {
      // All California - show all results in table (no additional filtering needed)
      setFilteredResults(allFilteredResultsMemo);
    } else {
      // Region-specific - all results are already for this region
      setFilteredResults(allFilteredResultsMemo);
    }
    
    console.log(`✅ Applied filters: ${allFilteredResultsMemo.length} PUMAs with results for ${getRegionName(selectedRegion)}`);
  }, [allFilteredResultsMemo, selectedRegion]);

  // Filter toggle handlers with debouncing
  const toggleFilter = useCallback((filterKey: keyof FilterState) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterKey]: !prev[filterKey]
      };
      
      // Debounce filter changes (wait 300ms after last change)
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
      
      filterDebounceRef.current = setTimeout(() => {
        setDebouncedFilters(newFilters);
      }, 300);
      
      return newFilters;
    });
  }, []);

  // Calculate summary metrics
  const totalTargetHouseholds = filteredResults.reduce((sum, result) => sum + result.filteredCount, 0);
  const totalHouseholds = filteredResults.reduce((sum, result) => sum + result.totalHouseholds, 0);
  const avgPercentage = totalHouseholds > 0 ? (totalTargetHouseholds / totalHouseholds) * 100 : 0;

  // Helper function to format a list of cities with proper grammar
  // Uses "&" for groupings to make multi-city PUMAs clearer
  const formatCityList = useCallback((cities: string): string => {
    // Clean up the input first - remove trailing "City", "Cities", "Town", etc. if present
    let cleaned = cities.trim();
    
    // Split by comma, "&", or "and" (case insensitive)
    // Use a regex that handles spaces around separators
    const cityArray = cleaned
      .split(/\s*,\s*|\s+&\s+|\s+and\s+/i)
      .map(city => {
        // Remove trailing "City", "Cities", "Town", "Towns" if present
        return city.trim().replace(/\s+(City|Cities|Town|Towns)\s*$/i, '').trim();
      })
      .filter(city => city.length > 0);
    
    if (cityArray.length === 0) return cities;
    if (cityArray.length === 1) return cityArray[0];
    if (cityArray.length === 2) return `${cityArray[0]} & ${cityArray[1]}`;
    
    // For 3+ cities, use "&" before the last city: "City1, City2 & City3"
    const lastCity = cityArray[cityArray.length - 1];
    const otherCities = cityArray.slice(0, -1).join(', ');
    return `${otherCities} & ${lastCity}`;
  }, []);

  // Helper function to extract area description from PUMA name
  // Handles multiple formats and formats city lists properly
  const extractAreaDescription = useCallback((pumaCode: number, pumaName: string): string => {
    // First, try to get the full name from GeoJSON (most comprehensive)
    const geoJsonName = getCountyFromPumaGeoJsonSync(pumaCode);
    
    let areaDescription = '';
    
    if (geoJsonName) {
      // Handle GeoJSON format: "County--Area Description" or "County (Location) - Area Description"
      // Extract part after "--" or after " - "
      const doubleDashIndex = geoJsonName.indexOf('--');
      if (doubleDashIndex !== -1) {
        areaDescription = geoJsonName.substring(doubleDashIndex + 2).trim();
        // Remove trailing dashes or spaces
        areaDescription = areaDescription.replace(/[-\s]+$/, '').trim();
      } else {
        // Handle format with " - " (space-dash-space)
        const dashIndex = geoJsonName.indexOf(' - ');
        if (dashIndex !== -1) {
          areaDescription = geoJsonName.substring(dashIndex + 3).trim();
        } else {
          // If it contains parentheses, try to extract meaningful part
          const parenMatch = geoJsonName.match(/\(([^)]+)\)/);
          if (parenMatch) {
            areaDescription = parenMatch[1].trim();
          } else {
            // If no special format, use the full GeoJSON name
            areaDescription = geoJsonName;
          }
        }
      }
    } else {
      // Fallback to pumaName (Bay Area format)
      const dashIndex = pumaName.indexOf(' - ');
      if (dashIndex !== -1) {
        areaDescription = pumaName.substring(dashIndex + 3).trim();
      } else if (pumaName.startsWith('PUMA ')) {
        areaDescription = `Area ${pumaCode}`;
      } else {
        areaDescription = pumaName;
      }
    }
    
    // Clean up common patterns and format city lists
    // Replace "&" with ", " for consistent formatting
    areaDescription = areaDescription
      .replace(/\s*&\s*/g, ', ')
      .trim();
    
    // Format multiple cities with proper grammar (formatCityList handles "City" suffix removal)
    return formatCityList(areaDescription);
  }, [formatCityList]);

  // Get top 3 PUMAs for dynamic text population
  const topThreePumas = useMemo(() => {
    return filteredResults.slice(0, 3).map(result => ({
      areaDescription: extractAreaDescription(result.puma, result.pumaName),
      pumaCode: result.puma,
      filteredCount: result.filteredCount,
      percentage: result.percentage
    }));
  }, [filteredResults, extractAreaDescription]);

  // Dynamic text template replacement function with emphasis
  const populateDynamicText = useCallback((template: string): string => {
    if (topThreePumas.length === 0) {
      // If no results, replace placeholders with generic text
      return template
        .replace(/{topArea1}/g, 'high-priority areas')
        .replace(/{topArea2}/g, 'target communities')
        .replace(/{topArea3}/g, 'priority locations');
    }

    // Emphasize the top 3 areas with bold formatting
    const replacements: Record<string, string> = {
      '{topArea1}': `<strong>${topThreePumas[0]?.areaDescription || 'high-priority areas'}</strong>`,
      '{topArea2}': `<strong>${topThreePumas[1]?.areaDescription || 'target communities'}</strong>`,
      '{topArea3}': `<strong>${topThreePumas[2]?.areaDescription || 'priority locations'}</strong>`,
    };

    let result = template;
    Object.entries(replacements).forEach(([placeholder, value]) => {
      result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return result;
  }, [topThreePumas]);

  // Memoize pumaResults calculation (expensive operation - only recalculate when allFilteredResults or rawData changes)
  const pumaResults: PumaResult[] = useMemo(() => {
    if (allFilteredResults.length === 0 || pumaRecordsMap.size === 0) return [];
    
    return allFilteredResults.map(result => {
      // Use pre-computed map for faster lookups
      const pumaRecords = pumaRecordsMap.get(result.puma) || [];
      
      let totalEnergyBurden = 0;
      let totalRentBurden = 0;
      let energyBurdenCount = 0;
      let rentBurdenCount = 0;
      
      pumaRecords.forEach(record => {
        const energyBurden = calculateEnergyBurden(record);
        if (energyBurden !== null && energyBurden !== undefined && !isNaN(energyBurden)) {
          totalEnergyBurden += energyBurden;
          energyBurdenCount++;
        }
        
        if (record.ten === 2 && record.grpip !== null && record.grpip !== undefined && record.grpip > 0) {
          totalRentBurden += record.grpip;
          rentBurdenCount++;
        }
      });
      
      const avgEnergyBurden = energyBurdenCount > 0 ? totalEnergyBurden / energyBurdenCount : 0;
      const avgRentBurden = rentBurdenCount > 0 ? totalRentBurden / rentBurdenCount : 0;
      
      return {
        puma: result.puma,
        pumaName: result.pumaName,
        county: result.county,
        totalHouseholds: result.totalHouseholds,
        targetHouseholds: result.filteredCount,
        targetPercentage: result.percentage,
        avgEnergyBurden: avgEnergyBurden,
        avgRentBurden: avgRentBurden,
      };
    });
  }, [allFilteredResults, pumaRecordsMap, calculateEnergyBurden]);

  if (loading) {
    const loadingMessage = selectedRegion === 0 
      ? 'Loading all California PUMS data. This process may take around 30 seconds...'
      : `Loading PUMS data for ${getRegionName(selectedRegion)}...`;
    
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#666',
        gap: '15px'
      }}>
        <div>{loadingMessage}</div>
        {selectedRegion === 0 && (
          <div style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
            Loading all 281 California PUMAs...
          </div>
        )}
        {/* Loading Progress Bar */}
        <div style={{ width: '300px', marginTop: '10px' }}>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            backgroundColor: '#e0e0e0', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${loadingProgress}%`, 
              height: '100%', 
              backgroundColor: '#4CAF50',
              transition: 'width 0.3s ease',
              borderRadius: '4px'
            }} />
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            marginTop: '8px',
            textAlign: 'center'
          }}>
            {loadingProgress}%
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px',
        fontSize: '16px',
        color: '#dc2626',
        textAlign: 'center',
        padding: '20px'
      }}>
        Error loading data: {error}
      </div>
    );
  }

  return (
    <ChartLayout
      title="Multifamily Energy Efficiency Targeting Tool - California Data"
      description="Interactive California-wide targeting tool using PUMS data to identify multifamily households for energy-efficiency program design."
      chartType="bar"
      layout="vertical"
      activeView={activeView}
      onViewChange={setActiveView}
      keyMetrics={
        <KeyMetrics>
          <KeyMetric
            value={filteredResults.length.toString()}
            label="PUMAs with Results"
            color="#1e3a8a"
          />
          <KeyMetric
            value={`${(totalTargetHouseholds / 1000).toFixed(0)}K`}
            label="Target Households"
            color="#059669"
          />
          <KeyMetric
            value={`${avgPercentage.toFixed(1)}%`}
            label="Target Percentage"
            color="#7c3aed"
          />
          <KeyMetric
            value={`${(totalHouseholds / 1000).toFixed(0)}K`}
            label="Total Households"
            color="#f59e0b"
          />
        </KeyMetrics>
      }
      chartContent={
        <div style={{ width: '100%' }}>
          {/* Region Selector */}
          <div style={{ 
            backgroundColor: '#ffffff', 
            padding: '20px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '2px solid #3b82f6',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <RegionSelector
              selectedRegion={selectedRegion}
              onRegionChange={handleRegionChange}
            />
          </div>
          
          {/* All California regions are supported */}
          <>
            {/* Filter Controls */}
            <div style={{ 
              backgroundColor: '#f8fafc', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #e2e8f0'
            }}>
          <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#1e293b' }}>
            Filter Criteria
          </h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            {/* Multifamily Unit Size */}
            <div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Unit Size</h5>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.unitsUnder5}
                  onChange={() => toggleFilter('unitsUnder5')}
                  style={{ marginRight: '6px' }}
                />
                Under 5 units
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.units5to19}
                  onChange={() => toggleFilter('units5to19')}
                  style={{ marginRight: '6px' }}
                />
                5-19 units
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.units20to50}
                  onChange={() => toggleFilter('units20to50')}
                  style={{ marginRight: '6px' }}
                />
                20-50 units
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.units50Plus}
                  onChange={() => toggleFilter('units50Plus')}
                  style={{ marginRight: '6px' }}
                />
                50+ units
              </label>
            </div>

            {/* Vintage */}
            <div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Vintage</h5>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.vintagePre1980}
                  onChange={() => toggleFilter('vintagePre1980')}
                  style={{ marginRight: '6px' }}
                />
                Pre-1980
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.vintage1980to2000}
                  onChange={() => toggleFilter('vintage1980to2000')}
                  style={{ marginRight: '6px' }}
                />
                1980-2000
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.vintage2000Plus}
                  onChange={() => toggleFilter('vintage2000Plus')}
                  style={{ marginRight: '6px' }}
                />
                2000+
              </label>
            </div>

            {/* Heating Fuel */}
            <div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Heating Fuel</h5>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.heatingElectric}
                  onChange={() => toggleFilter('heatingElectric')}
                  style={{ marginRight: '6px' }}
                />
                Electric
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.heatingGas}
                  onChange={() => toggleFilter('heatingGas')}
                  style={{ marginRight: '6px' }}
                />
                Natural Gas
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.heatingOther}
                  onChange={() => toggleFilter('heatingOther')}
                  style={{ marginRight: '6px' }}
                />
                Other
              </label>
            </div>

            {/* Tenure */}
            <div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Tenure</h5>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.ownerOccupied}
                  onChange={() => toggleFilter('ownerOccupied')}
                  style={{ marginRight: '6px' }}
                />
                Owner Occupied
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.renterOccupied}
                  onChange={() => toggleFilter('renterOccupied')}
                  style={{ marginRight: '6px' }}
                />
                Renter Occupied
              </label>
            </div>

            {/* Rent Burden */}
            <div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Rent Burden</h5>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.lowRentBurden}
                  onChange={() => toggleFilter('lowRentBurden')}
                  style={{ marginRight: '6px' }}
                />
                Low (&lt; 30%)
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.highRentBurden}
                  onChange={() => toggleFilter('highRentBurden')}
                  style={{ marginRight: '6px' }}
                />
                High (≥ 30%)
              </label>
            </div>

            {/* Energy Burden */}
            <div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Energy Burden</h5>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.lowEnergyBurden}
                  onChange={() => toggleFilter('lowEnergyBurden')}
                  style={{ marginRight: '6px' }}
                />
                Low (&lt; 6%)
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.highEnergyBurden}
                  onChange={() => toggleFilter('highEnergyBurden')}
                  style={{ marginRight: '6px' }}
                />
                High (≥ 6%)
              </label>
            </div>

            {/* Demographics */}
            <div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>Demographics (Householder)</h5>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.seniorHouseholder}
                  onChange={() => toggleFilter('seniorHouseholder')}
                  style={{ marginRight: '6px' }}
                />
                Senior (65+)
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.pocHouseholder}
                  onChange={() => toggleFilter('pocHouseholder')}
                  style={{ marginRight: '6px' }}
                />
                Person of Color
              </label>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                <input 
                  type="checkbox" 
                  checked={filters.lowEducation}
                  onChange={() => toggleFilter('lowEducation')}
                  style={{ marginRight: '6px' }}
                />
                Less than College
              </label>
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            backgroundColor: '#f8fafc', 
            padding: '12px 16px', 
            borderBottom: '1px solid #e2e8f0',
            fontWeight: '600'
          }}>
            PUMA Results ({filteredResults.length} areas)
          </div>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f1f5f9', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>PUMA</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>County</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Target Households</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Total Households</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((result, index) => (
                  <tr key={result.puma} style={{ 
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                    borderBottom: '1px solid #f1f5f9'
                  }}>
                    <td style={{ padding: '12px 16px', fontWeight: '500' }}>
                      {result.puma}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {result.county}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                      {result.filteredCount.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      {result.totalHouseholds.toLocaleString()}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      {result.percentage.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* PUMA Map */}
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px',
          marginTop: '20px',
          padding: '20px'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#1e293b' }}>
            Geographic Distribution of Target Households
          </h4>
          <p style={{ marginBottom: '15px', color: '#64748b', fontSize: '14px' }}>
            PUMA areas are color-coded by the percentage of households meeting your selected criteria. 
            Click on any PUMA area to see detailed information.
          </p>
          <BayAreaCountyMap 
            pumaResults={pumaResults}
            loading={loading}
            selectedRegion={selectedRegion}
            useAllPumas={selectedRegion === 0}
          />
        </div>
          </>
        </div>
      }
      overviewContent={
        <OverviewContent
          title="Program Targeting for Multifamily Housing Energy Efficiency"
          description={populateDynamicText("Public Use Microdata Sample (PUMS) data is collected by the US Census Bureau and contains anonymized data about persons and households sorted by geographic regions called Public Use Microdata Areas (PUMAs). <br> <br>This interactive tool allows you to filter household data by various criteria to identify households that meet specific targeting requirements for energy efficiency programs. Household energy efficiency is a key metric for reducing GHG emissions statewide, allowing policymakers to target the most in-need communities based on information about housing stock and demographic characteristics.<br> <br>Based on your current filter selections, the top three highest priority areas are {topArea1}, {topArea2}, and {topArea3}.")}
        >
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to Energy Efficiency Program Implementation"
          description="Multifamily retrofit programs are often hard to target because of owner-tenant dynamics and shared-building logistics. This tool helps prioritize geographies based on program criteria and local context."
        >
          <div className="methodology-details-simple">
            <h5>Program Design</h5>
            <p>
              Identifies specific PUMA areas with high concentrations of target households, enabling 
              Organizations to design geographically-focused programs that maximize impact and efficiency. 
              Understanding the scale of potential participants helps set realistic program goals.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity Considerations in Program Targeting"
          description="Low-income and disadvantaged households are more likely to face inefficient housing and high utility burden. This tool supports equity-focused targeting using economic and demographic filters."
        >
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="This analysis uses California-wide Census Public Use Microdata Sample (PUMS) data to provide accurate household-level targeting information."
          dataSources={[
            'Census Public Use Microdata Sample (PUMS) - ACS 2018-2022 5-Year Housing and Person files',
            'Supabase pums_combined_ca table - Processed PUMS data for all California households'
          ]}
          methodology={[
            'Load complete California PUMS dataset using Census statistical weights (WGTP)',
            'Apply user-selected filters to identify households meeting targeting criteria',
            'Filter by selected region using PUMA codes',
            'Calculate weighted household counts for each PUMA area',
            'Sort results by target household count for program prioritization',
            'Display results in interactive table with percentage calculations',
            'Update results dynamically as filters are modified'
          ]}
        />
      }
      showRelevanceEquity={true}
    />
  );
};

export default CaliforniaBayRENTool;